// キャンペーン進行のロジック (Phase 4.5 リデザイン)。
// 設計のキモ:
//  - 「死にゲーは自分で気づく」を尊重: チュートリアル的ヒントは廃止
//  - 代わりに「達成の可視化」 (目撃した攻撃名 / 戦績数値) で報酬を出す
// 🔴 廃止 (2026-07-29): 死亡数で解放する世界観ロウ (loreFragments / revealedLoreByBossId /
//   getRevealedLore / しきい値 1敗3敗7敗)。表示する経路が src に無く、死亡回数を条件に使わない
//   方針とも衝突していた。退避 = `docs/departments/scenario/_archived/2026-07-29-lore-fragments-retired.md`
//   現行方針 = `docs/candidates/2026-07-29-achievement-flavor-text-design.md` §9

import type { CampaignState, EndingChoice } from './types'
import { BOSS_DEFINITIONS, isFinalBossIndex } from './bosses'

export function createCampaign(): CampaignState {
  return {
    currentBossIndex: 0,
    defeatedBossIds: [],
    deathsByBossId: {},
    seenAttacksByBossId: {},
    parrySuccessByBossId: {},
    parryFailByBossId: {},
    dodgeByBossId: {},
    counterByBossId: {},
    justParryByBossId: {},
    guardByBossId: {},
    totalDeaths: 0,
    timeByBossId: {},
    totalTime: 0,
    awakening: 0,
    seenScenes: [],
    choicesByBossId: {},
  }
}

// 「下書き」の進行 (稽古・dev フック用)。 まっさらな進行に scratch 印を立てるだけ =
//   saveCampaign が入口で弾く (persistence.ts) ため、 この進行がどこで何回保存されようと
//   本物のセーブへは絶対に書かれない (2026-07-26 稽古セーブ破壊修正)。
export function createScratchCampaign(): CampaignState {
  return { ...createCampaign(), scratch: true }
}

// タイトルに「続きから」を出すか (2026-10-19)。 下書き (稽古) は進み具合に数えない。
//   どれか1つでも createCampaign() から動いていれば「続きがある」= 最初の会話を見ただけ・一度死んだだけでも出す。
export function hasCampaignProgress(c: CampaignState): boolean {
  if (c.scratch) return false
  const nonEmpty = (m: Record<string, unknown> | undefined): boolean => Object.keys(m ?? {}).length > 0
  return c.currentBossIndex > 0
    || c.defeatedBossIds.length > 0
    || (c.seenScenes ?? []).length > 0
    || (c.totalDeaths ?? 0) > 0
    || (c.totalTime ?? 0) > 0
    || (c.awakening ?? 0) !== 0
    || nonEmpty(c.seenAttacksByBossId)
    || nonEmpty(c.choicesByBossId)
}

// プレイテスト計器: 1挑戦の経過秒をボス別累積 + 通算へ足し込む (死亡・撃破 共通)。
//   旧セーブにフィールドが無くても ?? でガード。 elapsedSec<=0 / NaN は無視。
function addBossTime(campaign: CampaignState, bossId: string, elapsedSec: number): {
  timeByBossId: Record<string, number>
  totalTime: number
} {
  const add = Number.isFinite(elapsedSec) && elapsedSec > 0 ? elapsedSec : 0
  const prevByBoss = campaign.timeByBossId ?? {}
  const prevTotal = campaign.totalTime ?? 0
  if (add === 0) return { timeByBossId: prevByBoss, totalTime: prevTotal }
  return {
    timeByBossId: { ...prevByBoss, [bossId]: (prevByBoss[bossId] ?? 0) + add },
    totalTime: prevTotal + add,
  }
}

// 撃破タイムの簡易表示 (m:ss)。 秒は2桁ゼロ埋め。 負値/NaN は 0:00。
export function formatClearTime(sec: number): string {
  const s = Number.isFinite(sec) && sec > 0 ? Math.floor(sec) : 0
  const m = Math.floor(s / 60)
  const rem = s % 60
  return `${m}:${rem.toString().padStart(2, '0')}`
}

// ボス攻撃を「目撃」したと記録 (テレグラフ開始時に呼ぶ)
export function markAttackSeen(campaign: CampaignState, bossId: string, attackId: string): CampaignState {
  const list = campaign.seenAttacksByBossId[bossId] ?? []
  if (list.includes(attackId)) return campaign
  return {
    ...campaign,
    seenAttacksByBossId: {
      ...campaign.seenAttacksByBossId,
      [bossId]: [...list, attackId],
    },
  }
}

export function recordParrySuccess(campaign: CampaignState, bossId: string): CampaignState {
  return {
    ...campaign,
    parrySuccessByBossId: {
      ...campaign.parrySuccessByBossId,
      [bossId]: (campaign.parrySuccessByBossId[bossId] ?? 0) + 1,
    },
  }
}

export function recordParryFail(campaign: CampaignState, bossId: string): CampaignState {
  return {
    ...campaign,
    parryFailByBossId: {
      ...campaign.parryFailByBossId,
      [bossId]: (campaign.parryFailByBossId[bossId] ?? 0) + 1,
    },
  }
}

export function recordDodge(campaign: CampaignState, bossId: string): CampaignState {
  return {
    ...campaign,
    dodgeByBossId: {
      ...campaign.dodgeByBossId,
      [bossId]: (campaign.dodgeByBossId[bossId] ?? 0) + 1,
    },
  }
}

export function recordCounter(campaign: CampaignState, bossId: string): CampaignState {
  return {
    ...campaign,
    counterByBossId: {
      ...campaign.counterByBossId,
      [bossId]: (campaign.counterByBossId[bossId] ?? 0) + 1,
    },
  }
}

// ジャストパリィ (2026-07-30 追加)。 通常パリィと二重には数えない ==
//   呼ぶ側 (game.ts applyCombatStats) が parrySuccess とは別のシグナルで呼び分ける。
//   ジャスト1回 = parrySuccess +1 かつ justParry +1 (justParry は parrySuccess の部分集合)。
//   旧セーブは justParryByBossId が undefined なので spread で {} に倒す。
export function recordJustParry(campaign: CampaignState, bossId: string): CampaignState {
  return {
    ...campaign,
    justParryByBossId: {
      ...campaign.justParryByBossId,
      [bossId]: (campaign.justParryByBossId?.[bossId] ?? 0) + 1,
    },
  }
}

// 長押しガードで「弾ける近接」を受けた数 (2026-07-30 追加)。 パリィではないので parrySuccess には入らない。
export function recordGuard(campaign: CampaignState, bossId: string): CampaignState {
  return {
    ...campaign,
    guardByBossId: {
      ...campaign.guardByBossId,
      [bossId]: (campaign.guardByBossId?.[bossId] ?? 0) + 1,
    },
  }
}

// プレイヤー死亡 (死亡数と溶かした時間だけを積む。 死亡数で解放する報酬は 2026-07-29 に廃止)
//   elapsedSec = この挑戦の経過秒 (溶かした時間として累積。 省略時は計上しない=後方互換)
export function onPlayerDeath(campaign: CampaignState, bossId: string, elapsedSec = 0): CampaignState {
  return {
    ...campaign,
    deathsByBossId: { ...campaign.deathsByBossId, [bossId]: (campaign.deathsByBossId[bossId] ?? 0) + 1 },
    totalDeaths: campaign.totalDeaths + 1,
    ...addBossTime(campaign, bossId, elapsedSec),
  }
}

// 撃破時: 撃破リストに追加 (重複なし)
//   elapsedSec = 勝った挑戦の経過秒 (死亡分の累積に上乗せ。 省略時は計上しない=後方互換)
export function onBossDefeat(campaign: CampaignState, bossId: string, elapsedSec = 0): CampaignState {
  const newDefeated = campaign.defeatedBossIds.includes(bossId)
    ? campaign.defeatedBossIds
    : [...campaign.defeatedBossIds, bossId]
  return {
    ...campaign,
    defeatedBossIds: newDefeated,
    ...addBossTime(campaign, bossId, elapsedSec),
  }
}

// 配布版 (v0.5 友達版) がどのボスで終わるか。 ここを撃破して幕間を読み終えると
// 「つづく」カードへ抜け、 その先のボスへは進まない (未完成の先を配らないため)。
//   2026-11-01 作者「ほぼ完成している介錯と覚醒獣は https://lunaire-game.pages.dev/ でプレイできるようにしてほしい」で
//   花嫁 → 覚醒獣へ動かした (亡霊→王→騎士→花嫁→介錯→覚醒獣の6戦)。その先 (剣舞以降) は `/?fight=<id>` でだけ起動する。
//   null = 区切りなし = 全ボスを通しで遊ぶ (開発時の通しプレイ。 dev フック __demoEnd で切替可)。
export const DEMO_FINAL_BOSS_ID: string | null = 'crowned-king-awoken'

let demoFinalBossId: string | null = DEMO_FINAL_BOSS_ID
export function setDemoFinalBossId(id: string | null): void { demoFinalBossId = id }
export function getDemoFinalBossId(): string | null { return demoFinalBossId }

// 「この撃破で配布版の区切りに来たか」。 幕間を読み終えた時点で判定する。
export function isDemoBoundary(defeatedBossId: string): boolean {
  return demoFinalBossId !== null && defeatedBossId === demoFinalBossId
}

export function advanceToNextBoss(campaign: CampaignState): CampaignState {
  // 配布版の区切りのボスは、 撃破しても次へ進めない (進めると未完成の先が遊べてしまう)。
  if (isDemoBoundary(BOSS_DEFINITIONS[campaign.currentBossIndex]?.id ?? '')) return campaign
  const next = Math.min(BOSS_DEFINITIONS.length - 1, campaign.currentBossIndex + 1)
  return { ...campaign, currentBossIndex: next }
}

export function isCampaignCleared(campaign: CampaignState): boolean {
  const currentId = BOSS_DEFINITIONS[campaign.currentBossIndex]?.id
  return isFinalBossIndex(campaign.currentBossIndex) &&
         !!currentId && campaign.defeatedBossIds.includes(currentId)
}

// 目覚め度の加算 (下限0)。 ADV選択の confirm 時に game.ts から呼ぶ。
export function addAwakening(c: CampaignState, delta: number): CampaignState {
  return { ...c, awakening: Math.max(0, (c.awakening ?? 0) + delta) }
}

// 先代撃破後の選択と目覚め度から終幕種別を決める (ワークブック ## 終幕 の分岐条件)。
//   choiceIndex 0 = 城に残って次の姫になる / 1 = 舞台を降りて城を出る。
//   True は「2択8体すべてで True (awakening 8/8)」の時だけ。 時間切れは選択・awakeningに関係なく Loop-2。
export const AWAKENING_TRUE_THRESHOLD = 8
export function resolveEnding(choiceIndex: number, awakening: number, timedOut: boolean): EndingChoice {
  if (timedOut) return 'loop2'
  if (choiceIndex === 0) return 'stay'
  return awakening >= AWAKENING_TRUE_THRESHOLD ? 'true' : 'loop'
}

// ボスごとの2択の結果を記録 (interlude の choice 確定時に game.ts から呼ぶ)。
//   同じボスに再度呼ぶと上書き (周回・リトライで最新の選択だけが残る)。
export function recordBossChoice(c: CampaignState, bossId: string, choice: 'true' | 'loop'): CampaignState {
  return { ...c, choicesByBossId: { ...(c.choicesByBossId ?? {}), [bossId]: choice } }
}

// シーン既読マーク (重複なし)。
export function markSceneSeen(c: CampaignState, sceneId: string): CampaignState {
  const seen = c.seenScenes ?? []
  if (seen.includes(sceneId)) return c
  return { ...c, seenScenes: [...seen, sceneId] }
}
